// The batch pen of the monster bodies (R2719, ui-map-interface.md §6.1.15):
// the `MonsterPen` of `render/monster-bodies.ts` over the instance batches
// of a layer.
//
// A capsule and a disc go to the segment batch with round caps (a disc is
// a capsule of length 0), so every filled part keeps the order the body
// draws it in, on any surface. A ring, an arc and a dashed ring go to the
// circle batch, and a glow to the quad batch as the soft mass cell
// `kraken-blur` tinted in the NPC ink. The monster layer flushes the quads
// first, then the segments, then the circles, so a glow stands under a
// body and a ring stands on it. The two inks are the NPC
// ink and its deep edge (`NPC_INK`, R1797). The monster layer, the round
// layer (the quill pod) and the lair point draw through it.

import { NPC_INK } from '../../gen/presentation';
import { DARK, TransformPen, type MonsterInk } from '../../render/monster-bodies';
import { F_ROUND } from '../layer';
import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';

/** The atlas cell a glow is drawn with: the soft mass of the kraken. */
export const GLOW_KEY = 'kraken-blur';

export class BatchPen extends TransformPen {
  /** A fade over everything the pen draws now: the hull's own alpha. */
  alpha = 1;

  constructor(
    private readonly segs: SegmentSink,
    private readonly circles: CircleSink,
    private readonly quads: QuadSink | null,
    private readonly glowCell: SpriteEntry | null,
  ) {
    super();
  }

  private ink(ink: MonsterInk): number { return ink === DARK ? NPC_INK.edge : NPC_INK.color; }

  protected emitCap(x0: number, y0: number, x1: number, y1: number, w: number, ink: MonsterInk, a: number): void {
    this.segs.line(x0, y0, x1, y1, w, this.ink(ink), a * this.alpha, F_ROUND);
  }

  protected emitDisc(x: number, y: number, r: number, ink: MonsterInk, a: number): void {
    this.segs.line(x, y, x, y, 2 * r, this.ink(ink), a * this.alpha, F_ROUND);
  }

  protected emitRing(x: number, y: number, r: number, w: number, ink: MonsterInk, a: number): void {
    this.circles.ring(x, y, r, w, this.ink(ink), a * this.alpha);
  }

  protected emitArc(x: number, y: number, r: number, w: number, start: number, sweep: number, ink: MonsterInk, a: number): void {
    this.circles.arc(x, y, r, w, start, sweep, this.ink(ink), a * this.alpha);
  }

  protected emitDashed(x: number, y: number, r: number, w: number, count: number, fill: number, start: number, ink: MonsterInk, a: number): void {
    this.circles.dashed(x, y, r, w, count, fill, this.ink(ink), a * this.alpha, start);
  }

  protected emitGlow(x: number, y: number, r: number, a: number): void {
    const cell = this.glowCell;
    if (!this.quads || !cell || cell.w <= 0) return;
    this.quads.sprite(x, y, 0, cell, NPC_INK.color, a * this.alpha, (2 * r) / cell.w);
  }
}
